<template>
  <div class="quotations-public-view">
    <div v-if="!loading && !error">
      <QuotationChangeStatusModal
          v-if="interactMode && showInteractModal"
          :interact-mode="interactMode"
          ref="quotation-interact-modal"
          @accept-quotation="_acceptQuotation"
          @reject-quotation="_rejectQuotation"
          @close="closeInteractModal"
      />
      <TitleBar>
        <template v-slot:title>
          <div class="title-status-wrap">
            <div>{{ $gettext('Quotation') }} {{ quotation.number }}</div>
            <StatusBadge :display-status="quotation.display_status"/>
          </div>
        </template>
        <template v-slot:buttons>
          <div v-if="!loading" class="top-buttons-wrap">
            <GekkoButton
                v-if="showActionButtons"
                @click="showAcceptModal"
                :text="$gettext('Accept quotation')"
                icon="fa-solid fa-check"
            />
            <GekkoButton
                v-if="showActionButtons"
                @click="showRejectModal"
                :text="$gettext('Reject quotation')"
                icon="fa-solid fa-xmark"
                variant="outline-danger"
            />
            <GekkoButton
                v-if="quotationPDF"
                @click="downloadQuotation"
                :text="$gettext('Download')"
                icon="fa-solid fa-download"
                variant="outline-primary"
            />
          </div>
        </template>
      </TitleBar>
      <div class="content">
        <div class="quotations-public-section">
          <h3>
            <font-awesome-icon icon="fa-solid fa-paperclip"/>
            {{ $gettext('Documents & Terms') }}
          </h3>
          <div>
            <AttachedDocumentList
                :object-id="quotation.id"
                :object-type="'Quotation'"
                :document-list="quotation.documents"
                public-documents
            />
          </div>
        </div>
        <div class="quotations-public-section">
          <h3>
            <font-awesome-icon icon="fa-regular fa-file-lines"/>
            {{ $gettext('Pro forma invoice') }}
            <GekkoButton
                v-if="quotationPDF"
                @click="downloadQuotation"
                icon="fa-solid fa-download"
                variant="link"
                class="text-blue"
            />
          </h3>
          <div class="pdf-preview-wrap" style="position: relative;">
            <LoadingSpinner v-if="loadingPDF" :display-text="$gettext('Generating preview')"/>
            <iframe v-if="!forceMobileViewer && !showMobileViewer && !loadingPDF && quotationPDF" id="pdf-frame"
                    class="pdf-preview" :src="quotationPDF + '#toolbar=0&view=1'"></iframe>
            <GkPDFViewer v-if="(forceMobileViewer || showMobileViewer) && !loadingPDF && quotationPDF"
                         :item-pdf="quotationPDF"/>
          </div>
        </div>
      </div>
    </div>

    <div class="container" v-else-if="loading">
      <div class="dialog">
        <h1>{{ this.$gettext("Fetching some data...") }}</h1>
        <span>{{ this.$gettext("This will only take a moment.") }}</span>
      </div>
    </div>

    <div class="container" v-else>
      <div class="dialog">
        <h1>{{ this.$gettext("Something went wrong") }}</h1>
        <span>{{ error }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import pdfviewer from "@/mixins/pdfviewer";

import TitleBar from "@/components/TitleBar"
import GekkoButton from '@/components/GekkoButton'
import QuotationChangeStatusModal from "@/components/quotations/ui/QuotationChangeStatusModal"
import AttachedDocumentList from "@/components/documents/AttachedDocumentList"
import StatusBadge from "@/components/invoices/ui/StatusBadge"
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'

import QuotationService from "@/store/services/quotation.service.js"

import GkPDFViewer from '@/components/invoices/ui/GkPDFViewer'

export default {
  name: 'QuotationsPublicView',
  mixins: [pdfviewer],
  components: {
    TitleBar,
    GekkoButton,
    QuotationChangeStatusModal,
    AttachedDocumentList,
    StatusBadge,
    LoadingSpinner,
    GkPDFViewer
  },
  data() {
    return {
      loading: false,
      error: null,
      loadingPDF: true,
      quotationUUID: null,
      showInteractModal: false,
      interactMode: null,
      quotation: null,
      quotationPDF: null
    }
  },
  computed: {
    showActionButtons() {
      return this.quotation && this.quotation.status === 'shared'
    }
  },
  created() {
    this.quotationUUID = this.$route.params['uuid']
    if (this.quotationUUID) {
      this.setupData()
    }
  },
  mounted() {
  },
  methods: {
    setupData() {
      this.loading = true

      QuotationService.retrieveQuotationPublic(this.quotationUUID, {extended: 'True'})
          .then((response) => {
            this.quotation = response.data

            this.getQuotationPDF()
          })
          .catch((error) => {
            if (error?.status !== 404) {
              console.log('error fetching', error)
              this.error = this.$gettext("An unknown error occurred.")
            } else {
              this.error = this.$gettext("The requested quotation was not found.")
            }
          })
          .finally(() => {
            this.loading = false
          })
    },
    closeInteractModal() {
      this.showInteractModal = false
    },
    showAcceptModal() {
      this.showInteractModal = true
      this.interactMode = 'accept'
    },
    showRejectModal() {
      this.showInteractModal = true
      this.interactMode = 'reject'
    },
    downloadQuotation() {
      const downloadLink = document.createElement('a')
      downloadLink.href = window.URL.createObjectURL(this.quotationBLOB)
      downloadLink.download = this.$gettext('Quotation') + '_' + this.quotation.number + '.pdf'
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    },
    async getQuotationPDF() {
      this.loadingPDF = true
      QuotationService.getQuotationPDFBlob(this.quotationUUID).then((response) => {
        this.quotationBLOB = response.data
        this._blobToBase64PDF(this.quotationBLOB)
      }).catch((error) => {
        console.log('error generating preview', error)
      })
    },
    _blobToBase64PDF(blob) {
      const reader = new FileReader()
      reader.onload = () => {
        this.quotationPDF = reader.result.replace('application/octet-stream', 'application/pdf');
        this.loadingPDF = false
      };
      reader.readAsDataURL(blob);
    },
    _acceptQuotation(client_name) {
      QuotationService.acceptQuotationPublic(this.quotationUUID, {
        sender_company: this.quotation.sender_company,
        actor: client_name
      }).then((response) => {
        this.showInteractModal = false
        this.setupData()
      }).catch((error) => {
        console.log('error', error)
      })
    },
    _rejectQuotation(client_name) {
      QuotationService.rejectQuotationPublic(this.quotationUUID, {
        sender_company: this.quotation.sender_company,
        actor: client_name
      }).then((response) => {
        this.showInteractModal = false
        this.setupData()
      }).catch((error) => {
        console.log('error', error)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  flex: 1;
  align-content: center;
}

.dialog {
  // The centering on this is hacky, maybe someone else can figure out a less hacky way sometime...
  width: 350px;
  margin: auto;
}

.quotations-public-view {
  .title-status-wrap {
    display: flex;
    gap: 1rem;

    .status-badge {
      font-size: 1rem;
    }
  }

  .top-buttons-wrap {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    align-items: center;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    .quotations-public-section {
      border-bottom: 1px solid $colorBorderDash;
    }
  }

  .pdf-preview-wrap {
    padding: 0;
    aspect-ratio: 1 / 1.4142; // for portrait A4
    max-width: 800px; // old styling
    margin: 0 auto;

    .pdf-preview {
      height: 100%;
      width: 100%;
      border: 0;
      transition: opacity 0.3s ease;
    }
  }
}

</style>
